import { Injectable } from '@angular/core';
import { pick } from 'lodash';
import { Observable } from 'rxjs';
import { skip, tap, withLatestFrom } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import {
  CalendarFiltersActionTypes,
  CalendarFilterSet,
  CalendarFilterSetAll
} from '../calendar-filter/actions';
import {
  BenchmarkCurvesActionTypes,
  SelectCurve
} from '../benchmark-curves/actions';
import {
  LOAD_WIRE_CONTENT_SUCCESS,
  LoadWireContentSuccess
} from '../wires/actions';
import { CalendarFiltersState } from '../calendar-filter/state';
import { SharedState, selectCalendarFiltersState } from '../reducer';
import {
  PendoDealMonitorExport,
  PendoDealManagerExport,
  PendoDealShowExport,
  PendoDealCalendarExport,
  PendoDealShowBidResultsOpened,
  PendoDealShowTabActivated,
  PendoTrackingActionTypes,
  PendoDealShowPrint,
  PendoDealShowLoaded,
  PendoDealCalendarWeekChange,
  PendoDealCalendarFilterSetSaved,
  PendoBidCompExport
} from './actions';
import { LoadMonitorSuccess, MonitorActionTypes } from '../monitor/actions';
import {
  CalendarActionTypes,
  LoadCalendarSuccess,
  SetSavedFilter,
  SetFlexColumn
} from '../calendar/actions';
import {
  CalendarSortingActionTypes,
  CalendarSortingSetModel
} from '../calendar-sorting/actions';

@Injectable()
export class PendoEffects {
  @Effect({ dispatch: false })
  public trackDealCalendarFilterChanges$: Observable<any> = this.actions$.pipe(
    ofType(CalendarFiltersActionTypes.Set, CalendarFiltersActionTypes.SetMany),
    withLatestFrom(this.store.pipe(select(selectCalendarFiltersState))),
    tap(
      ([, filterState]: [
        CalendarFilterSet | CalendarFilterSetAll,
        CalendarFiltersState
      ]) => {
        trackPendo('mu-calendar-filtered', {
          filters: filterState as any
        });
      }
    )
  );

  @Effect({ dispatch: false })
  public trackDealCalendarSavedFilterLoaded$: Observable<SetSavedFilter> = this.actions$.pipe(
    ofType(CalendarActionTypes.SetSavedFilter),
    tap((action: SetSavedFilter) => {
      trackPendo('mu-deal-calendar-saved-filter-loaded', action.payload);
    })
  );

  @Effect({ dispatch: false })
  public trackDealCalendarSortChanges$: Observable<CalendarSortingSetModel> = this.actions$.pipe(
    ofType(CalendarSortingActionTypes.SetModel),
    tap((action: CalendarSortingSetModel) => {
      trackPendo('mu-deal-calendar-sort-changed', action.payload);
    })
  );

  @Effect({ dispatch: false })
  public trackDealCalendarFlexColumnChanged$: Observable<SetFlexColumn> = this.actions$.pipe(
    ofType(CalendarActionTypes.SetFlexColumn),
    skip(1),
    tap((action: SetFlexColumn) => {
      trackPendo('mu-deal-calendar-set-flex-column', action.payload);
    })
  );

  @Effect({ dispatch: false })
  public trackCurveChanges$: Observable<any> = this.actions$.pipe(
    ofType(BenchmarkCurvesActionTypes.SelectCurve),
    skip(1),
    tap((action: SelectCurve) => {
      trackPendo('mu-curve-selected', action.payload);
    })
  );

  @Effect({ dispatch: false })
  public trackWireLoad$: Observable<any> = this.actions$.pipe(
    ofType(LOAD_WIRE_CONTENT_SUCCESS),
    skip(1),
    tap((action: LoadWireContentSuccess) => {
      const metadata = {
        ...pick(
          action.payload,
          'issueId',
          'wireId',
          'wireType',
          'wireTitle',
          'public'
        ),
        bookManager:
          action.payload.bookManager && action.payload.bookManager.name
      };
      trackPendo('mu-wire-loaded', metadata);
    })
  );

  @Effect({ dispatch: false })
  public trackDealCalendarExport$: Observable<PendoDealCalendarExport> = this.actions$.pipe(
    ofType(PendoTrackingActionTypes.DealCalendarExport),
    tap((action: PendoDealCalendarExport) => {
      trackPendo('mu-deal-calendar-export', action.weekOf);
    })
  );

  @Effect({ dispatch: false })
  public trackDealShowExport$: Observable<PendoDealShowExport> = this.actions$.pipe(
    ofType(PendoTrackingActionTypes.DealShowExport),
    tap((action: PendoDealShowExport) => {
      trackPendo('mu-deal-show-export', action.dealId);
    })
  );

  @Effect({ dispatch: false })
  public trackDealMonitorExport$: Observable<PendoDealMonitorExport> = this.actions$.pipe(
    ofType(PendoTrackingActionTypes.DealMonitorExport),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tap((action: PendoDealMonitorExport) => {
      trackPendo('mu-deal-monitor-export', '');
    })
  );

  @Effect({ dispatch: false })
  public trackBidCompExport$: Observable<PendoBidCompExport> = this.actions$.pipe(
    ofType(PendoTrackingActionTypes.BidCompExport),
    tap(() => {
      trackPendo('mu-bidcomp-export', '');
    })
  );

  @Effect({ dispatch: false })
  public trackDealManagerExport$: Observable<PendoDealManagerExport> = this.actions$.pipe(
    ofType(PendoTrackingActionTypes.DealManagerExport),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tap((action: PendoDealManagerExport) => {
      trackPendo('mu-deal-manager-export', '');
    })
  );

  @Effect({ dispatch: false })
  public trackDealShowTabActivated$: Observable<PendoDealShowTabActivated> = this.actions$.pipe(
    ofType(PendoTrackingActionTypes.DealShowTabActivated),
    tap((action: PendoDealShowTabActivated) => {
      trackPendo('mu-deal-show-tab-activated', action.payload);
    })
  );

  @Effect({ dispatch: false })
  public trackDealShowBidResultsOpened$: Observable<PendoDealShowBidResultsOpened> = this.actions$.pipe(
    ofType(PendoTrackingActionTypes.DealShowBidResultsOpened),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tap((action: PendoDealShowBidResultsOpened) => {
      trackPendo('mu-deal-show-bid-results-opened', '');
    })
  );

  @Effect({ dispatch: false })
  public trackDealShowPrint$: Observable<PendoDealShowPrint> = this.actions$.pipe(
    ofType(PendoTrackingActionTypes.DealShowPrint),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tap((action: PendoDealShowPrint) => {
      trackPendo('mu-deal-show-print', '');
    })
  );

  @Effect({ dispatch: false })
  public trackDealShowLoaded$: Observable<PendoDealShowLoaded> = this.actions$.pipe(
    ofType(PendoTrackingActionTypes.DealShowLoaded),
    tap((action: PendoDealShowLoaded) => {
      trackPendo('mu-deal-show-loaded', action.dealId);
    })
  );

  @Effect({ dispatch: false })
  public trackDealCalendarLoaded$: Observable<LoadCalendarSuccess> = this.actions$.pipe(
    ofType(CalendarActionTypes.LoadCalendarSuccess),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tap((action: LoadCalendarSuccess) => {
      trackPendo('mu-deal-calendar-loaded', '');
    })
  );

  @Effect({ dispatch: false })
  public trackDealMonitorLoaded$: Observable<LoadMonitorSuccess> = this.actions$.pipe(
    ofType(MonitorActionTypes.LoadMonitorSuccess),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tap((action: LoadMonitorSuccess) => {
      trackPendo('mu-deal-monitor-loaded', '');
    })
  );

  @Effect({ dispatch: false })
  public trackDealCalendarWeekChange$: Observable<PendoDealCalendarWeekChange> = this.actions$.pipe(
    ofType(PendoTrackingActionTypes.DealCalendarWeekChange),
    tap((action: PendoDealCalendarWeekChange) => {
      trackPendo('mu-deal-calendar-week-change', action.weekOf);
    })
  );

  @Effect({ dispatch: false })
  public trackDealCalendarFilterSetSaved$: Observable<PendoDealCalendarFilterSetSaved> = this.actions$.pipe(
    ofType(PendoTrackingActionTypes.DealCalendarFilterSetSaved),
    tap((action: PendoDealCalendarFilterSetSaved) => {
      trackPendo('mu-deal-calendar-filter-set-saved', action.payload);
    })
  );

  constructor(private actions$: Actions, private store: Store<SharedState>) {}
}

function trackPendo(event: string, metadata: any) {
  if (
    typeof window['pendo'] === 'object' &&
    typeof window['pendo'].track === 'function'
  ) {
    // think about not writing this to console on prod
    console.log(`trackPendo: ${event} => ${JSON.stringify(metadata)}`);
    window['pendo'].track(event, metadata);
  }
}
